import PropTypes from 'prop-types';
import SolicitorItem from './solicitor-item';
import SolicitorDropdown from 'apps/portfolio-performance/components/solicitor-dropdown';
import useSolicitorTitles from 'apps/proposals/hooks/use-solicitor-titles';
import { useState } from 'react';
import { useDynamicFormContext } from '@evertrue/sdk';
import { useProposalFormContext } from '../../context/proposal-form-context';

// This functions similarly to the ProposalFormSolicitors component, but is used as a field in the DynamicForm.
// ProposalFormSolicitors is used in the LegacyProposalForm, while this is used in the new ProposalForm.
const SolicitorsField = ({ onChange, onBlur, name, value = [], required = false, field, meta, helpers, values }) => {
  const { solicitorLimit = 1 } = useProposalFormContext();
  const [defaultToUser, setDefaultToUser] = useState(!value?.length);
  const [solicitorTitles = []] = useSolicitorTitles();
  const { evaluateRulesForField } = useDynamicFormContext();
  const titleState = evaluateRulesForField('solicitor_title', values);

  // helper function to handle the update or removal of a solicitor in the array
  const handleItemChange = (id, data = {}) => {
    let updatedSolicitors = [...value];
    if (data.type) {
      // update solicitor type
      updatedSolicitors = value?.map((solicitor) => {
        if (solicitor.contact_id === id) {
          return { ...solicitor, ...data };
        } else {
          return { ...solicitor, type: 'SECONDARY' };
        }
      });
    } else if (data.title) {
      // update solicitor title
      updatedSolicitors = value?.map((solicitor) => {
        if (solicitor.contact_id === id) {
          return { ...solicitor, title: data.title };
        }
        return solicitor;
      });
    } else {
      // remove solicitor from the list
      setDefaultToUser(false); // if we are removing a solicitor, do not repopulate with the user
      updatedSolicitors = value?.filter((solicitor) => solicitor.contact_id !== id);
    }
    helpers.setValue(updatedSolicitors);
  };

  const getPrimaryIfNone = (solicitors) => {
    const solicitorsWithPrimary = [...solicitors];
    if (!solicitorsWithPrimary.length) return solicitorsWithPrimary;
    const primary = solicitorsWithPrimary.find((solicitor) => solicitor.type === 'PRIMARY');
    if (!primary) solicitorsWithPrimary[0].type = 'PRIMARY';
    return solicitorsWithPrimary;
  };

  const handleAddSolicitor = (solicitor) => {
    const solicitors = [
      ...value,
      {
        contact_id: solicitor.value,
        name: solicitor.label,
        type: 'SECONDARY',
      },
    ];

    const solicitorsWithPrimary = getPrimaryIfNone(solicitors);
    helpers.setValue(solicitorsWithPrimary);
  };

  return (
    <ul>
      {value?.map((solicitor) => (
        <SolicitorItem
          key={solicitor.contact_id}
          values={values}
          solicitor={solicitor}
          titleState={titleState}
          handleChange={handleItemChange}
          solicitorLimit={solicitorLimit}
          solicitorTitles={solicitorTitles}
        />
      ))}
      <li className="proposal-form--add-item-row">
        <div className="proposal-form--form-field-dropdown">
          <SolicitorDropdown
            placeholder="Select a solicitor to add..."
            label={`Select solicitor to add to proposal. You must add at least one and a max of ${solicitorLimit}`}
            onChange={(solicitor) => handleAddSolicitor(solicitor)}
            multiple={false}
            defaultToUser={defaultToUser}
            disabled={value?.length >= solicitorLimit}
            excludeIds={value?.map((solicitor) => solicitor.contact_id)}
          />
        </div>
      </li>
    </ul>
  );
};

SolicitorsField.propTypes = {
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  name: PropTypes.string,
  value: PropTypes.array,
  required: PropTypes.bool,
  field: PropTypes.shape({
    name: PropTypes.string,
    onBlur: PropTypes.func,
    onChange: PropTypes.func,
    value: PropTypes.any,
    checked: PropTypes.bool,
    multiple: PropTypes.bool,
  }),
  meta: PropTypes.shape({
    error: PropTypes.string,
    initialError: PropTypes.string,
    initialTouched: PropTypes.bool,
    initialValue: PropTypes.any,
    touched: PropTypes.bool,
    value: PropTypes.any,
  }),
  helpers: PropTypes.shape({
    setValue: PropTypes.func,
    setTouched: PropTypes.func,
    setError: PropTypes.func,
  }),
  values: PropTypes.object,
};

export default SolicitorsField;
